// frontend/src/components/admin/AdminUsers.js
import React, { useState, useEffect } from 'react';
import {
    User,
    Mail,
    Calendar,
    Trash2,
    Ban,
    CheckCircle,
    Eye,
    X,
    Search,
    Users as UsersIcon,
    RefreshCw,
    ArrowLeft,
    UserPlus,
    Edit,
    Package,
    MapPin,
    Phone,
    ChevronRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AdminUsers = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUserDetails, setLoadingUserDetails] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/v1/admin/users-list', {
                withCredentials: true
            });
            
            if (data.success) {
                setUsers(data.users);
            } else {
                toast.error("Failed to fetch users data");
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error(error.response?.data?.message || 'Error fetching users');
        } finally {
            setLoading(false);
        }
    };

    const handleView = async (userId) => {
        setLoadingUserDetails(true);
        try {
            console.log(`Fetching details for user ID: ${userId}`);
            
            const { data } = await axios.get(`/api/v1/admin/user/${userId}`, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            });
            
            console.log('API response:', data);
            
            if (data.success && data.user) {
                setSelectedUser({
                    ...data.user,
                    orders: data.orders || []
                });
                setShowViewModal(true);
            } else {
                toast.error('Failed to fetch user details');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to fetch user details';
            console.error("Error fetching user details:", error);
            toast.error(errorMsg);
        } finally {
            setLoadingUserDetails(false);
        }
    };

    const handleBlockUser = async (userId, isBlocked) => {
        try {
            const { data } = await axios.put(
                `/api/v1/admin/user/${userId}/block`,
                {},
                { withCredentials: true }
            );
            
            if (data.success) {
                toast.success(data.message);
                fetchUsers();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating user status');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                const { data } = await axios.delete(`/api/v1/admin/user/${userId}`, {
                    withCredentials: true
                });
                
                if (data.success) {
                    toast.success('User deleted successfully');
                    fetchUsers();
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error deleting user');
            }
        }
    };

    const handleCreateUser = () => {
        navigate('/admin/user/create');
    };

    const handleEditUser = (userId) => {
        navigate(`/admin/user/edit/${userId}`);
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const UserCard = ({ user }) => (
        <div className="bg-white rounded-lg shadow-sm p-3 mb-3 border border-gray-100">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    {user.avatar ? (
                        <img
                            src={user.avatar}
                            alt={user.name}
                            className="h-10 w-10 rounded-full object-cover"
                        />
                    ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-400" />
                        </div>
                    )}
                    <div>
                        <h3 className="font-medium text-sm text-gray-900">{user.name}</h3>
                        <div className="flex items-center text-xs text-gray-500">
                            <Mail className="h-3 w-3 mr-1" />
                            {user.email}
                        </div>
                    </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    user.isBlocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                }`}>
                    {user.isBlocked ? 'Blocked' : 'Active'}
                </span>
            </div>
           
            <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Joined {new Date(user.createdAt).toLocaleDateString()}
                </div>
                <div className="flex space-x-1.5">
                    <button
                        onClick={() => handleView(user._id)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                    >
                        <Eye className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => handleEditUser(user._id)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                    >
                        <Edit className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => handleBlockUser(user._id, user.isBlocked)}
                        className={`p-1.5 rounded-full transition-colors ${
                            user.isBlocked
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-red-600 hover:bg-red-50'
                        }`}
                    >
                        {user.isBlocked ? (
                            <CheckCircle className="h-4 w-4" />
                        ) : (
                            <Ban className="h-4 w-4" />
                        )}
                    </button>
                    <button
                        onClick={() => handleDeleteUser(user._id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );

    const UserViewModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
            <div className="bg-white rounded-xl shadow-lg p-4 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold text-[#dc2626] font-['Crimson_Text']">User Details</h2>
                    <button
                        onClick={() => setShowViewModal(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
               
                {loadingUserDetails ? (
                    <div className="flex justify-center items-center h-32">
                        <RefreshCw className="h-6 w-6 text-[#dc2626] animate-spin" />
                    </div>
                ) : selectedUser ? (
                    <div className="space-y-4">
                        {/* User Profile Section */}
                        <div className="flex items-center space-x-3">
                            {selectedUser.avatar ? (
                                <img
                                    src={selectedUser.avatar}
                                    alt={selectedUser.name}
                                    className="h-14 w-14 rounded-full object-cover"
                                />
                            ) : (
                                <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center">
                                    <User className="h-7 w-7 text-gray-400" />
                                </div>
                            )}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{selectedUser.name}</h3>
                                <p className="text-sm text-gray-600">{selectedUser.email}</p>
                                {selectedUser.phone && (
                                    <p className="text-xs text-gray-500 flex items-center mt-0.5">
                                        <Phone className="h-3 w-3 mr-1" />
                                        {selectedUser.phone}
                                    </p>
                                )}
                            </div>
                        </div>
                       
                        {/* User Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center space-x-2">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                    <div>
                                        <p className="text-xs text-gray-500">Join Date</p>
                                        <p className="font-medium text-sm">
                                            {new Date(selectedUser.createdAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center space-x-2">
                                    {selectedUser.isBlocked ? (
                                        <Ban className="h-4 w-4 text-red-500" />
                                    ) : (
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    )}
                                    <div>
                                        <p className="text-xs text-gray-500">Status</p>
                                        <p className={`font-medium text-sm ${
                                            selectedUser.isBlocked ? 'text-red-600' : 'text-green-600'
                                        }`}>
                                            {selectedUser.isBlocked ? 'Blocked' : 'Active'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Address Section */}
                        {selectedUser.address && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-start space-x-2">
                                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500">Address</p>
                                        <p className="font-medium text-sm text-gray-800">{selectedUser.address}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Order Statistics */}
                        {selectedUser.stats && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="p-3 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xs text-gray-500">Total Orders</p>
                                    <p className="font-semibold text-base text-gray-800">{selectedUser.stats.totalOrders || 0}</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xs text-gray-500">Total Spent</p>
                                    <p className="font-semibold text-base text-gray-800">₹{selectedUser.stats.totalSpent?.toFixed(2) || '0.00'}</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xs text-gray-500">Delivered</p>
                                    <p className="font-semibold text-base text-green-600">{selectedUser.stats.deliveredOrders || 0}</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg text-center">
                                    <p className="text-xs text-gray-500">Pending</p>
                                    <p className="font-semibold text-base text-blue-600">{selectedUser.stats.pendingOrders || 0}</p>
                                </div>
                            </div>
                        )}

                        {/* Recent Orders */}
                        {selectedUser.orders && selectedUser.orders.length > 0 && (
                            <div>
                                <h4 className="font-medium text-sm text-gray-800 mb-2 flex items-center">
                                    <Package className="h-4 w-4 mr-1.5 text-gray-500" />
                                    Recent Orders
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">Order ID</th>
                                                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">Date</th>
                                                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">Amount</th>
                                                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {selectedUser.orders.slice(0, 5).map(order => (
                                                <tr key={order._id}>
                                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs">
                                                        {order.orderId || order._id.substring(0, 8)}
                                                    </td>
                                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500">
                                                        {new Date(order.createdAt).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs">
                                                        ₹{order.totalPrice?.toFixed(2) || '0.00'}
                                                    </td>
                                                    <td className="px-3 py-1.5 whitespace-nowrap">
                                                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                                            order.orderStatus === 'Order Delivered' 
                                                                ? 'bg-green-100 text-green-800' 
                                                                : order.orderStatus === 'Order Cancelled'
                                                                ? 'bg-red-100 text-red-800'
                                                                : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                            {order.orderStatus}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {selectedUser.orders.length > 5 && (
                                        <div className="mt-3 text-center">
                                            <button
                                                onClick={() => {
                                                    setShowViewModal(false);
                                                    navigate(`/admin/user/${selectedUser._id}`);
                                                }}
                                                className="inline-flex items-center text-xs text-[#dc2626] hover:text-[#dc2626]/80"
                                            >
                                                View all {selectedUser.orders.length} orders
                                                <ChevronRight className="h-3 w-3 ml-1" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* No Orders */}
                        {(!selectedUser.orders || selectedUser.orders.length === 0) && (
                            <div className="text-center py-4 bg-gray-50 rounded-lg">
                                <Package className="h-8 w-8 text-gray-400 mx-auto mb-1.5" />
                                <p className="text-sm text-gray-500">No orders found for this user</p>
                            </div>
                        )}

                        <div className="border-t pt-4 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    handleEditUser(selectedUser._id);
                                }}
                                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                                Edit User
                            </button>
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="text-center text-gray-500 py-6">
                        <User className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                        <p className="text-sm">No user details available</p>
                    </div>
                )}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <RefreshCw className="h-6 w-6 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-3 py-6">
            {/* Back to Dashboard Button - Right aligned */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => navigate('/admin/dashboard')}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Dashboard</span>
                </button>
            </div>
                
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex items-center space-x-1.5">
                            <UsersIcon className="h-5 w-5 text-[#dc2626]" />
                            <h2 className="text-lg font-bold text-[#dc2626] font-['Crimson_Text']">
                                Users Management
                            </h2>
                        </div>
                       
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full md:w-56 pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-xs"
                                />
                                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                            </div>
                           
                            <button
                                onClick={handleCreateUser}
                                className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors text-sm"
                            >
                                <UserPlus className="w-4 h-4" />
                                <span>Create User</span>
                            </button>
                        </div>
                    </div>
                </div>
                {/* Desktop View */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Joined Date
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map((user) => (
                                <tr key={user._id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {user.avatar ? (
                                                <img
                                                    src={user.avatar}
                                                    alt={user.name}
                                                    className="h-8 w-8 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                                                    <User className="h-4 w-4 text-gray-400" />
                                                </div>
                                            )}
                                            <span className="ml-2 font-medium text-xs text-gray-900">{user.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center text-xs text-gray-600">
                                            <Mail className="h-3 w-3 mr-1.5" />
                                            {user.email}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center text-xs text-gray-600">
                                            <Calendar className="h-3 w-3 mr-1.5" />
                                            {new Date(user.createdAt).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                            user.isBlocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                        }`}>
                                            {user.isBlocked ? 'Blocked' : 'Active'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex space-x-1.5">
                                            <button
                                                onClick={() => handleView(user._id)}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEditUser(user._id)}
                                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleBlockUser(user._id, user.isBlocked)}
                                                className={`p-1.5 rounded-full transition-colors ${
                                                    user.isBlocked
                                                        ? 'text-green-600 hover:bg-green-50'
                                                        : 'text-red-600 hover:bg-red-50'
                                                }`}
                                            >
                                                {user.isBlocked ? (
                                                    <CheckCircle className="h-4 w-4" />
                                                ) : (
                                                    <Ban className="h-4 w-4" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteUser(user._id)}
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Mobile View */}
                <div className="md:hidden p-3">
                    {filteredUsers.map((user) => (
                        <UserCard key={user._id} user={user} />
                    ))}
                </div>
                {filteredUsers.length === 0 && (
                    <div className="text-center py-8">
                        <User className="mx-auto h-10 w-10 text-gray-400" />
                        <h3 className="mt-2 text-xs font-medium text-gray-900">No Users Found</h3>
                        <p className="mt-1 text-xs text-gray-500">
                            {searchTerm ? 'No users match your search criteria.' : 'No users have been created yet.'}
                        </p>
                    </div>
                )}
            </div>
            {showViewModal && <UserViewModal />}
        </div>
    );
};

export default AdminUsers;